import { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Alert,
    Switch,
    FormControlLabel,
    Slider,
    Collapse,
    IconButton,
    Typography
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { Localized } from "@fluent/react";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { AlgorithmSetting, AutoMatchingParameters } from "../ursOfferSlice";
import { CancelButton } from "../../../app/components/CancelButton";
import { YesButton } from "../../../app/components/YesButton";

interface AutoMatchDialogProps {
    saving: boolean;
    serverError: string;
    setServerError: (value: string) => void;
    handleConfirmAutoMatchClick: (params: AutoMatchingParameters) => void;
    handleDialogCancelClick: () => void;
}

const AutoMatchDialog: React.FC<AutoMatchDialogProps> = ({
    serverError,
    saving,
    setServerError,
    handleConfirmAutoMatchClick,
    handleDialogCancelClick
}) => {
    const [showAdvanced, setShowAdvanced] = useState(true);
    const [settings, setSettings] = useState<Record<"levenshtein" | "sbert", AlgorithmSetting>>({
        levenshtein: { enabled: true, level: 75 },
        sbert: { enabled: true, level: 75 },
    });

    const wasSaving = useRef(false);
    const [onlyFromSameStructure, setOnlyFromSameStructure] = useState(true);
    const [clearExistingComments, setClearExistingComments] = useState(false);

    const handleToggle = (key) => {
        setSettings(prev => ({
            ...prev,
            [key]: { ...prev[key], enabled: !prev[key].enabled }
        }));
    };

    const handleSliderChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: { ...prev[key], level: value }
        }));
    };

    const handleClickWithTimer = () => {

        wasSaving.current = true;

        const parameters = {
            levenshtein: settings.levenshtein,
            sbert: settings.sbert,
            onlyFromSameStructure: onlyFromSameStructure,
            clearExistingComments: clearExistingComments
        };
        handleConfirmAutoMatchClick(parameters);
    };


    return (
        <Dialog open={true}>
            <DialogContent>
                <DialogTitle>
                    <Localized id="urs-offer-matching-question">
                        <span>Czy chcesz aby system zaproponował powiązania między wymaganiami o ofertą?</span>
                    </Localized>
                </DialogTitle>
                <Alert severity="info">
                    <Localized id="urs-offer-matching-question-description">
                        <span>Auto matching</span>
                    </Localized>
                </Alert>

                {serverError && (
                    <Alert sx={{ marginTop: 1 }} onClose={() => setServerError("")} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}

                {/* Advanced Options Toggle */}
                <div style={{ display: "flex", alignItems: "center", marginTop: 10 }}>
                    <Typography variant="body1" sx={{ marginRight: 1 }}>Advanced Options</Typography>
                    <IconButton onClick={() => setShowAdvanced(!showAdvanced)}>
                        <ExpandMore sx={{ transform: showAdvanced ? "rotate(180deg)" : "rotate(0deg)" }} />
                    </IconButton>
                </div>

                <Collapse in={showAdvanced}>
                    <div style={{ marginTop: 10 }}>
                        {Object.keys(settings).map((key) => (
                            <div key={key} style={{ marginBottom: 10 }}>
                                <FormControlLabel
                                    control={<Switch checked={settings[key].enabled} onChange={() => handleToggle(key)} />}
                                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                                />
                                <Slider
                                    value={settings[key].level}
                                    onChange={(e, value) => handleSliderChange(key, value)}
                                    min={0}
                                    max={100}
                                    step={1}
                                    valueLabelDisplay="auto"
                                    disabled={!settings[key].enabled}
                                />
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: 10 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={onlyFromSameStructure}
                                    onChange={(e) => setOnlyFromSameStructure(e.target.checked)}
                                />
                            }
                            label="Only from same structure"
                        />
                    </div>
                    <div style={{ marginTop: 10 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={clearExistingComments}
                                    onChange={(e) => setClearExistingComments(e.target.checked)}
                                />
                            }
                            label="Clear existing comments"
                        />
                    </div>
                </Collapse>
            </DialogContent>
            <DialogActions>
                <YesButton loading={saving} onClick={handleClickWithTimer} />
                <CancelButton loading={saving} onClick={handleDialogCancelClick} />
            </DialogActions>
        </Dialog>
    );
};

export default AutoMatchDialog;
